// Big-number helper. Formats large quantities with letter notation:
//   K, M, B, T  then  AA, AB, AC ... AZ, BA ... ZZ, AAA ...
//
// MVP decision: values are stored as plain JS doubles. Doubles are exact to
// ~15-16 significant digits and overflow only past 1e308, which is far beyond
// anything reachable in Phase 1 to 3. All formatting and scaling goes through
// this module, so if we ever need a mantissa+exponent Decimal type (to kill
// precision loss when adding a tiny producer to a huge total), we swap it here
// without touching gameplay code. See README "Numbers" note.

const SHORT_SUFFIXES = ['', 'K', 'M', 'B', 'T'] as const;

// Cache generated alphabetic suffixes so repeated formatting stays cheap.
const alphaCache: string[] = [];

/**
 * Maps a 0-based index to the alphabetic suffix sequence that starts at "AA".
 *   0 -> AA, 1 -> AB, ... 25 -> AZ, 26 -> BA, ... 675 -> ZZ, 676 -> AAA ...
 * The suffix length grows from 2 letters upward, so the range is effectively
 * unbounded for any value a double can hold.
 */
function alphaSuffix(index: number): string {
  const cached = alphaCache[index];
  if (cached !== undefined) return cached;

  let i = index;
  let len = 2;
  let combos = 26 * 26;
  while (i >= combos) {
    i -= combos;
    len++;
    combos *= 26;
  }
  let s = '';
  for (let k = 0; k < len; k++) {
    s = String.fromCharCode(65 + (i % 26)) + s;
    i = Math.floor(i / 26);
  }
  alphaCache[index] = s;
  return s;
}

/** Returns the suffix for a 1000^tier magnitude tier (tier 1 = K, 5 = AA). */
function suffixForTier(tier: number): string {
  if (tier < SHORT_SUFFIXES.length) return SHORT_SUFFIXES[tier];
  return alphaSuffix(tier - SHORT_SUFFIXES.length);
}

/** Trims trailing zeros (and a dangling dot) from a fixed-decimal string. */
function trim(n: number, decimals: number): string {
  let s = n.toFixed(decimals);
  if (s.indexOf('.') >= 0) s = s.replace(/\.?0+$/, '');
  return s;
}

/**
 * Formats a number for display. Below 1000 it shows the plain value with a
 * decimal count that shrinks as the magnitude grows; at and above 1000 it
 * switches to letter notation with two scaled significant decimals.
 */
export function fmt(value: number): string {
  if (!isFinite(value)) {
    // NaN renders as '0' (safe display); +Infinity as '∞'; -Infinity as '0'.
    if (Number.isNaN(value)) return '0';
    return value === Infinity ? '∞' : '0';
  }
  if (value < 0) return '-' + fmt(-value);
  if (value < 1) {
    if (value === 0) return '0';
    // sub-unit values (rates, fractions) keep two decimals
    return trim(value, 2);
  }
  if (value < 1000) {
    const decimals = value < 10 ? 2 : value < 100 ? 1 : 0;
    // If it rounds up to 1000 (e.g. 999.9), fall through to the suffix path
    // so it shows "1K" instead of a bare "1000".
    if (Number(value.toFixed(decimals)) < 1000) return trim(value, decimals);
  }

  let tier = Math.floor(Math.log10(value) / 3);
  let scaled = value / Math.pow(1000, tier);
  // guard against floating-point edge cases at exact powers of 1000
  if (scaled >= 1000) {
    tier++;
    scaled /= 1000;
  } else if (scaled < 1) {
    tier--;
    scaled *= 1000;
  }
  let decimals = scaled < 10 ? 2 : scaled < 100 ? 1 : 0;
  // a value like 999999 scales to 999.999 and rounds to "1000" at this tier;
  // carry it into the next band so it reads "1M" rather than "1000K".
  if (Number(scaled.toFixed(decimals)) >= 1000) {
    tier++;
    scaled /= 1000;
    decimals = scaled < 10 ? 2 : scaled < 100 ? 1 : 0;
  }
  return trim(scaled, decimals) + suffixForTier(tier);
}

/** Formats a per-second rate, e.g. "12.3/s". Zero renders cleanly as "0/s". */
export function fmtRate(value: number): string {
  return fmt(value) + '/s';
}

/** Formats a whole-number count (population, levels) without decimals. */
export function fmtInt(value: number): string {
  if (value < 1000) return Math.floor(value).toString();
  return fmt(Math.floor(value));
}

/** Formats a duration in seconds as a compact "1h 23m" / "45s" string. */
export function fmtDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = s % 60;
    return rem ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return remM ? `${h}h ${remM}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH ? `${d}d ${remH}h` : `${d}d`;
}

/** Clamp helper used across systems. */
export function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
